//frontend/src/pages/auth/Register.jsx
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loginSettingsAPI, publicAPI } from '../../services/api';
import '../../assets/styles/register.css';

const Register = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    company_name: '',
    register_number: '',
    email: '',
    phone: '',
    address: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Success modal
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  
  // Settings
  const [logoUrl, setLogoUrl] = useState('/logo.png');

  // Fetch settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await loginSettingsAPI.getLoginSettings();
        if (response.success && response.data?.logoUrl) {
          let logo = response.data.logoUrl;
          if (typeof logo === 'string' && logo.startsWith('"')) {
            try { logo = JSON.parse(logo); } catch (e) {}
          }
          setLogoUrl(logo);
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const getLogoSrc = () => {
    if (!logoUrl) return '/logo.png';
    if (logoUrl.startsWith('http')) return logoUrl;
    if (logoUrl.startsWith('/uploads')) {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';
      return `${apiUrl.replace('/api/v1', '')}${logoUrl}`;
    }
    return logoUrl;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, logo: 'Зөвхөн зураг оруулна уу' }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, logo: 'Файл 5MB-аас бага байх ёстой' }));
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setErrors(prev => ({ ...prev, logo: '' }));
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Компанийн нэр оруулна уу';
    }
    
    if (!formData.register_number.trim()) {
      newErrors.register_number = 'РД оруулна уу';
    } else if (!/^\d{7}$/.test(formData.register_number)) {
      newErrors.register_number = '7 оронтой тоо';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'И-мэйл оруулна уу';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'И-мэйл буруу байна';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Утас оруулна уу';
    } else if (!/^\d{8}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = '8 оронтой дугаар';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const data = new FormData();
      data.append('company_name', formData.company_name);
      data.append('register_number', formData.register_number);
      data.append('email', formData.email);
      data.append('phone', formData.phone);
      if (formData.address) data.append('address', formData.address);
      if (logoFile) data.append('logo', logoFile);
      
      await publicAPI.registerCompany(data);
      setSubmittedEmail(formData.email);
      setShowSuccess(true);
      
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.message || 'Бүртгэхэд алдаа гарлаа';
      setErrors({ submit: message });
    } finally {
      setLoading(false);
    }
  };

  // Success Modal
  const SuccessModal = () => (
    <div className="reg-modal-overlay">
      <div className="reg-modal">
        <div className="reg-modal-icon">✓</div>
        <h2 className="reg-modal-title">Бүртгэл амжилттай!</h2>
        <p className="reg-modal-text">Таны бүртгэл хүлээн авлаа</p>
        
        <div className="reg-modal-email-box">
          <span className="reg-modal-email-icon">📧</span>
          <div>
            <p className="reg-modal-email-label">И-мэйл хаяг:</p>
            <p className="reg-modal-email-value">{submittedEmail}</p>
          </div>
        </div>
        
        <div className="reg-modal-info">
          <p>⏱️ 24 цагийн дотор и-мэйл ирнэ</p>
          <p>📱 Spam хавтсаа шалгана уу</p>
          <p>🔐 Нэвтрэх мэдээлэл илгээгдэнэ</p>
        </div>
        
        <div className="reg-modal-buttons">
          <button onClick={() => navigate('/login')} className="reg-modal-btn-primary">
            Нэвтрэх хуудас
          </button>
          <button 
            onClick={() => {
              setShowSuccess(false);
              setFormData({ company_name: '', register_number: '', email: '', phone: '', address: '' });
              setLogoFile(null);
              setLogoPreview(null);
            }} 
            className="reg-modal-btn-secondary"
          >
            Өөр бүртгэл
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="register-page">
      {showSuccess && <SuccessModal />}
      
      {/* Left Side - Blue */}
      <div className="register-left">
        {/* Decorative circles */}
        <div className="register-circle circle-1"></div>
        <div className="register-circle circle-2"></div>
        <div className="register-circle circle-3"></div>
        
        {/* Logo */}
        <div className="register-logo">
          <img src={getLogoSrc()} alt="Logo" />
        </div>
      </div>
      
      {/* Right Side - Form */}
      <div className="register-right">
        <div className="register-form-box">
          {/* Header */}
          <div className="register-header">
            <h1>Компани бүртгүүлэх</h1>
            <p>Мэдээллээ оруулаад системд бүртгүүлээрэй</p>
          </div>
          
          {/* Error */}
          {errors.submit && (
            <div className="register-error-box">
              <span>⚠️</span> {errors.submit}
            </div>
          )}
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="register-form">
            {/* Row 1: Company Name + RD */}
            <div className="register-row">
              <div className="register-field">
                <label>Компанийн нэр</label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder="Номин Холдинг"
                  className={errors.company_name ? 'error' : ''}
                />
                {errors.company_name && <span className="field-error">{errors.company_name}</span>}
              </div>
              
              <div className="register-field small">
                <label>Регистр (РД)</label>
                <input
                  type="text"
                  name="register_number"
                  value={formData.register_number}
                  onChange={handleChange}
                  placeholder="1234567"
                  maxLength="7"
                  className={errors.register_number ? 'error' : ''}
                />
                {errors.register_number && <span className="field-error">{errors.register_number}</span>}
              </div>
            </div>
            
            {/* Row 2: Phone + Email */}
            <div className="register-row">
              <div className="register-field">
                <label>Утасны дугаар</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="99001122"
                  maxLength="8"
                  className={errors.phone ? 'error' : ''}
                />
                {errors.phone && <span className="field-error">{errors.phone}</span>}
              </div>
              
              <div className="register-field">
                <label>И-мэйл хаяг</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="info@company.mn"
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>
            </div>
            
            {/* Row 3: Address + Logo */}
            <div className="register-row">
              <div className="register-field">
                <label>Хаяг (заавал биш)</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="УБ хот, СБД..."
                />
              </div>
              
              <div className="register-field small">
                <label>Лого</label>
                {logoPreview ? (
                  <div className="logo-preview-box">
                    <img src={logoPreview} alt="Logo" />
                    <button type="button" onClick={removeLogo}>✕</button>
                  </div>
                ) : (
                  <label className="logo-upload-box">
                    <input type="file" accept="image/*" onChange={handleLogoChange} />
                    <span>📷 Сонгох</span>
                  </label>
                )}
                {errors.logo && <span className="field-error">{errors.logo}</span>}
              </div>
            </div>
            
            {/* Buttons */}
            <div className="register-buttons">
              <button type="submit" disabled={loading} className="btn-register">
                {loading ? 'Бүртгэж байна...' : 'Бүртгүүлэх'}
              </button>
              
              <Link to="/login" className="btn-signin">
                Нэвтрэх
              </Link>
            </div>
          </form>
          
          {/* Footer */}
          <div className="register-footer">
            <p>© {new Date().getFullYear()} Pingo Express</p>
            <p>
              Developed by{' '}
              <a href="mailto:info@prolink.mn">Prolink Team</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;